import { useState } from "react";
import { localDateAt, localTimeAt, type ScheduleEntry } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { CancelIcon, MoveIcon } from "./icons.tsx";

/**
 * Отмена и перенос РАЗОВОГО занятия.
 *
 * Отдельным файлом от `EntryTools.tsx` с 30.08.2026, и повод тот же, что у
 * `OverrideForm.tsx`: вместе они переросли потолок в 400 строк (раздел 9
 * CLAUDE.md) в тот день, когда сводной строке группы вернули инструменты.
 * Граница проходит по ручкам, а не по размеру: у разового занятия правится САМ
 * УРОК (`moveSingleLesson`, `trashLesson`), а у занятия серии пишется строка
 * исключения, и общего между этими двумя путями нет ничего, кроме вида кнопок.
 *
 * ⚠️ **Двух наборов полей даты и времени в проекте не заводится.** Этот набор
 * не второй, а тот же самый, что стоял здесь до переезда: у занятия серии он
 * остался в `EntryTools.tsx`, и групповое занятие ходит через него же,
 * подставляя другой адрес ручки. Третьего набора нет и заводить его не надо.
 *
 * ⚠️ Исключению у разового занятия висеть не на чем: ключа занятия
 * (`slotId` плюс дата) у него нет вовсе, поэтому «вернуть на место» и «сделать
 * особым» здесь отсутствуют не по замыслу интерфейса, а по устройству данных.
 *
 * Возвращается ФРАГМЕНТ, а не своя коробка: ряд действий держит карточка, там
 * же разобрано почему.
 */
export function SingleEntryTools({
  entry,
  zone,
  busy,
  run,
}: {
  entry: ScheduleEntry;
  zone: string;
  busy: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
}) {
  /**
   * У разового занятия серии нет, и зона это зона смотрящего: он задавал время
   * именно в ней, когда занятие заводил.
   */
  const formZone = entry.seriesTimeZone ?? zone;
  const [mode, setMode] = useState<"none" | "cancel" | "move">("none");
  const [toDate, setToDate] = useState(() => localDateAt(entry.at, formZone));
  const [toTime, setToTime] = useState(() => localTimeAt(entry.at, formZone));

  if (entry.lessonId === null) return null;
  const lessonId = entry.lessonId;

  return (
    <>
      {mode === "none" && (
        <>
          {/*
            Перенос обычным значком: занятие остаётся, меняется только его
            время. Отмена красная, потому что для ученика она необратима:
            занятие исчезло из его расписания, и вернуть его преподаватель
            может только новым занятием. Цвет тут не заливка, а обводка и
            линия значка: это ещё предложение, а не подтверждение, оно только
            разворачивает форму ниже
          */}
          <button
            type="button"
            className="btn btn--ico btn--quiet"
            disabled={busy}
            title="Перенести на другой день или час"
            aria-label="Перенести занятие"
            onClick={() => setMode("move")}
          >
            <MoveIcon />
          </button>
          <button
            type="button"
            className="btn btn--ico btn--danger"
            disabled={busy}
            title="Отменить занятие"
            aria-label="Отменить занятие"
            onClick={() => setMode("cancel")}
          >
            <CancelIcon />
          </button>
        </>
      )}

      {mode === "move" && (
        <div className="form form--row">
          <label className="field field--tight">
            <span className="lbl">Дата</span>
            <input
              className="fld"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>
          <label className="field field--tight">
            <span className="lbl">Время</span>
            <input
              className="fld"
              type="time"
              value={toTime}
              onChange={(e) => setToTime(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="btn"
            disabled={busy || toDate === ""}
            onClick={() =>
              void run(async () => {
                await api.moveSingleLesson(lessonId, {
                  date: toDate,
                  time: toTime,
                  timeZone: formZone,
                });
                setMode("none");
              })
            }
          >
            Перенести
          </button>
          <button
            type="button"
            className="btn btn--quiet"
            disabled={busy}
            onClick={() => setMode("none")}
          >
            Не надо
          </button>
          <p className="muted">Время в зоне {formZone}. Материал и доска переезжают вместе.</p>
        </div>
      )}

      {mode === "cancel" && (
        <div className="note">
          <p>
            Разовое занятие отменяется вместе со своим уроком: он уедет в корзину, тридцать дней
            восстановим, материал и доска на месте.
          </p>
          <div className="form__foot">
            <button
              type="button"
              className="btn btn--quiet"
              disabled={busy}
              onClick={() => setMode("none")}
            >
              Не надо
            </button>
            {/*
              Шаг подтверждения: следующее нажатие уносит урок в корзину.
              Заливка и слова, а не значок: значок хорош для предложения, а
              это уже подтверждение необратимого, и оно обязано читаться
            */}
            <button
              type="button"
              className="btn btn--danger is-confirm"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api.trashLesson(lessonId);
                  setMode("none");
                })
              }
            >
              Отменить занятие
            </button>
          </div>
        </div>
      )}
    </>
  );
}
